
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type EntityType = 'candidates' | 'jobs' | 'companies';

// Define interfaces for entity data types
interface BaseEntity {
  id: string;
}

interface CandidateEntity extends BaseEntity {
  name: string;
  function?: string;
  skills?: string[];
  ai_synthesis?: string;
}

interface JobEntity extends BaseEntity {
  title?: string;
  description?: string;
  skills?: string[];
}

interface CompanyEntity extends BaseEntity {
  name: string;
  description?: string;
}

// Define a type for our database records
type EntityRecord = CandidateEntity | JobEntity | CompanyEntity;

export const GenerateEmbeddingsButton = ({ 
  entityType, 
  onComplete 
}: { 
  entityType: EntityType;
  onComplete?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const generateEmbeddings = async () => {
    setIsLoading(true);
    setProgress(0);
    
    try {
      // First, get records without specifically checking embedding column
      // as it might not exist yet in some tables
      const { data, error } = await supabase
        .from(entityType)
        .select('*')
        .limit(100);
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        toast({
          title: "No records found",
          description: `No ${entityType} found in database.`,
        });
        setIsLoading(false);
        return;
      }
      
      toast({
        title: "Generating Embeddings",
        description: `Processing ${data.length} ${entityType}...`,
      });
      
      // Process each record
      let successCount = 0;
      let failCount = 0;
      
      for (let i = 0; i < data.length; i++) {
        // Verify we have a valid record object
        if (!data[i] || typeof data[i] !== 'object' || !('id' in data[i])) {
          console.error(`Invalid record at index ${i}:`, data[i]);
          failCount++;
          continue;
        }
        
        // Use type assertion with a temporary variable after validation
        const record = data[i] as unknown as EntityRecord;
        setProgress(Math.floor((i / data.length) * 100));
        
        // Build text content for embeddings based on entity type
        let textContent = "";
        
        switch (entityType) {
          case 'candidates':
            {
              // Safely cast the record to the appropriate type
              const candidate = record as CandidateEntity;
              textContent = [
                candidate.name,
                candidate.function,
                candidate.skills ? candidate.skills.join(" ") : "",
                candidate.ai_synthesis || ""
              ].filter(Boolean).join(" ");
            }
            break;
            
          case 'jobs':
            {
              // Safely cast the record to the appropriate type
              const job = record as JobEntity;
              textContent = [
                job.title,
                job.description,
                job.skills ? job.skills.join(" ") : ""
              ].filter(Boolean).join(" ");
            }
            break;
            
          case 'companies':
            {
              // Safely cast the record to the appropriate type
              const company = record as CompanyEntity;
              textContent = [
                company.name,
                company.description || ""
              ].filter(Boolean).join(" ");
            }
            break;
        }
        
        if (textContent.trim().length === 0) {
          console.warn(`No text content for ${entityType} ${record.id}`);
          failCount++;
          continue;
        }
        
        try {
          // Call our embeddings generation function
          const { error: genError } = await supabase.functions.invoke("generate-embeddings", {
            body: {
              text: textContent,
              table: entityType,
              recordId: record.id
            }
          });
          
          if (genError) {
            console.error(`Error generating embeddings for ${entityType} ${record.id}:`, genError);
            failCount++;
          } else {
            successCount++;
          }
        } catch (genError) {
          console.error(`Exception generating embeddings for ${entityType} ${record.id}:`, genError);
          failCount++;
        }
      }
      
      toast({
        title: "Embeddings Generation Complete",
        description: `Successfully processed ${successCount} records with ${failCount} failures.`,
      });
      
      if (onComplete) {
        onComplete();
      }
      
    } catch (error) {
      console.error("Error generating embeddings:", error);
      toast({
        title: "Error",
        description: "Failed to generate embeddings. See console for details.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setProgress(100);
    }
  };

  return (
    <Button 
      onClick={generateEmbeddings} 
      disabled={isLoading}
      variant="secondary"
      className="gap-2"
    >
      {isLoading && (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {progress}%
        </>
      )}
      Generate {entityType} embeddings
    </Button>
  );
};
